<template>
  <div>
    <app-header></app-header>
    <div class="container-fluid admin-dashboard">
      <div class="row">
        <div class="col-md-3 col-sm-12 pr-0 dark-bg">
          <app-admin-sidebar></app-admin-sidebar>
        </div>
        <transition appear name="fade">
          <div class="col-md-9 p-4">
            <div :style="{ margin:'50px' }">
              <div class="card mb-4">
                <div 
                  class="card-header bg-light d-flex justify-content-between align-items-center"
                  style="cursor:pointer;"
                  @click="showFormula = !showFormula"
                >
                  <b>📊 Formula Reference</b>
                  <span>
                    <i :class="showFormula ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                  </span>
                </div>
                <div class="card-body" v-show="showFormula">

                  <!-- ================= POINTS ================= -->
                  <div class="mb-4">
                    <h5 class="text-primary">POINTS</h5>
                    <div class="row text-sm">

                      <div class="col-md-4 mb-3">
                        <b>Allocated Points</b>
                        <p class="mb-0 text-muted">
                          Points added within selected date range
                        </p>
                        <code>
                          Points Added + API Points Added
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Used Points</b>
                        <p class="mb-0 text-muted">
                          Net points used in orders after adjusting credits
                        </p>
                        <code>
                          (Order + Order Tax/Income Tax + Sales Tax) − (Credits + Credit Tax + Credit Sales Tax)
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Remaining Points</b>
                        <p class="mb-0 text-muted">
                          Current available points across all users
                        </p>
                        <code>
                          Sum of all User Points
                        </code>
                      </div>

                    </div>
                  </div>

                  <!-- ================= REVENUE ================= -->
                  <div class="mb-4">
                    <h5 class="text-success">REVENUE ($)</h5>
                    <div class="row text-sm">

                      <div class="col-md-4 mb-3">
                        <b>Physical Orders Count</b>
                        <p class="mb-0 text-muted">
                          Total number of physical item orders
                        </p>
                        <code>Count(No. Of Orders)</code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>COGS (Physical)</b>
                        <p class="mb-0 text-muted">
                          Cost of physical item orders
                        </p>
                        <code>
                          Quantity × (Wholesale + Shipping) + Sales Tax
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Revenue Generated (Physical)</b>
                        <p class="mb-0 text-muted">
                          Revenue from physical item orders
                        </p>
                        <code>
                          (Points / Ratio) for Orders + Sales Tax
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Physical Items Refunds</b>
                        <p class="mb-0 text-muted">
                          Refunds on physical orders
                        </p>
                        <code>
                          (Points / Ratio) for Credits + Credit Sales Tax
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Physical Order Tax</b>
                        <p class="mb-0 text-muted">
                          Tax applied on physical orders
                        </p>
                        <code>
                          (Points / Ratio) for Order Tax OR Income Tax
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Physical Sales Tax</b>
                        <p class="mb-0 text-muted">
                          Sales tax on physical items
                        </p>
                        <code>
                          (Points / Ratio) for Sales Tax
                        </code>
                      </div>

                      <!-- GIFT CARDS -->

                      <div class="col-md-4 mb-3">
                        <b>Gift Card Orders Count</b>
                        <p class="mb-0 text-muted">
                          Total number of gift card orders
                        </p>
                        <code>Count(No. Of Orders)</code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>COGS (Gift Cards)</b>
                        <p class="mb-0 text-muted">
                          Cost of gift card orders(always ordered as a single quantity)
                        </p>
                        <code>
                          Wholesale Price
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Revenue Generated (Gift Cards)</b>
                        <p class="mb-0 text-muted">
                          Revenue from gift card orders
                        </p>
                        <code>
                          (Points / Ratio) for Orders
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Gift Card Returns</b>
                        <p class="mb-0 text-muted">
                          Refunds on gift cards
                        </p>
                        <code>
                          (Points / Ratio) for Credits
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Gift Card Order Tax</b>
                        <p class="mb-0 text-muted">
                          Tax applied on gift cards
                        </p>
                        <code>
                          (Points / Ratio) for Order Tax OR Income Tax
                        </code>
                      </div>

                      <!-- COMBINED -->

                      <div class="col-md-4 mb-3">
                        <b>Total Order Tax</b>
                        <p class="mb-0 text-muted">
                          Combined tax from all orders
                        </p>
                        <code>
                          Physical Order Tax + Gift Card Order Tax
                        </code>
                      </div>

                    </div>
                  </div>

                  <!-- ================= PROFIT ================= -->
                  <div>
                    <h5 class="text-warning">PROFIT ($)</h5>
                    <div class="row text-sm">

                      <div class="col-md-4 mb-3">
                        <b>COGS</b>
                        <p class="mb-0 text-muted">
                          Net cost after adjusting orders and credits
                        </p>
                        <code>
                          (Order: Quantity × (Wholesale + Shipping) + Sales Tax)
                          − (Credit: Quantity × (Wholesale + Shipping) + Sales Tax)
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Revenue Generated</b>
                        <p class="mb-0 text-muted">
                          Net revenue including tax and refunds
                        </p>
                        <code>
                          (Points / Ratio for Orders + Sales Tax)
                          − (Points / Ratio for Credit + Credit Sales Tax)
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Profit</b>
                        <p class="mb-0 text-muted">
                          Net earnings after cost deduction
                        </p>
                        <code>
                          Revenue Generated − COGS
                        </code>
                      </div>

                      <div class="col-md-4 mb-3">
                        <b>Profit %</b>
                        <p class="mb-0 text-muted">
                          Profit margin based on revenue
                        </p>
                        <code>
                          ((Revenue − COGS) / Revenue) × 100
                        </code>
                      </div>

                    </div>
                  </div>

                </div>
              </div>
              <div class="card">
                <div class="card-header">
                  <b>Search Section</b>
                </div>
                <div class="card-body">
                    <form>
                      <div class="row">
                        <div class="col">
                          <b style="text-transform: capitalize;">From:</b>
                          <input
                            type="date"
                            class="form-control"
                            v-model="fromDate"
                            placeholder="From Date"
                          />
                        </div>
                        <div class="col">
                          <b style="text-transform: capitalize;">To:</b>
                          <input
                            type="date"
                            class="form-control"
                            v-model="toDate"
                            placeholder="To Date"
                          />
                        </div>
                        <div class="col">
                          <b style="text-transform: capitalize;">Report Type:</b>
                          <select class="form-control" v-model="reportType">
                            <option value="">Select Report Type</option>
                            <option value="total">Total</option>
                            <option value="client">Client</option>
                          </select>
                        </div>
                        <div class="col">
                          <b style="text-transform: capitalize;">Clients:</b>
                          <select class="form-control " id="clients-dropdown" v-model="clientIds" multiple >
                            <option v-for="data in allClients" :value="data.clientId.toString()" :key="data.clientId">{{ data.clientName }}</option>
                          </select>
                        </div>
                      </div>
                      <div class="row pt-2 text-center">
                        <div class="col">
                          <button class="btn btn-primary" type="button" :disabled="isDisabled" @click="getOverallMetrics()">Search <i class="fas fa-search"></i>
                            <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
                          </button>
                        </div>
                      </div>
                    </form>
                </div>
              </div>
              <div class="row" v-if="overallMatrics">
                <div class="col-xl-4 col-md-6 mb-4">
                    <div class="card border-left-primary shadow h-100 py-2">
                      <a href="javascript:void(0)" @click="pointMetrics()">
                        <div class="card-body">
                            <div class="row no-gutters align-items-center">
                              <div class="col mr-2">
                                  <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                    Point
                                  </div><br>
                                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                                    <small>Point Allocation : ${{  overallMatrics.point_allocated_in_dollars }}</small>
                                  </div>
                              </div>
                            </div>
                        </div>
                      </a>
                    </div>
                </div>
                <div class="col-xl-4 col-md-6 mb-4">
                    <div class="card border-left-primary shadow h-100 py-2">
                      <a href="javascript:void(0)" @click="revenueMetrics()">
                        <div class="card-body">
                            <div class="row no-gutters align-items-center">
                              <div class="col mr-2">
                                  <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                    Revenue
                                  </div><br>
                                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                                    <small>Revenue : ${{  overallMatrics.revenue }}</small><br>
                                    <small>COGS : ${{  overallMatrics.cogs }}</small>
                                  </div>
                              </div>
                            </div>
                        </div>
                      </a>
                    </div>
                </div>
                <div class="col-xl-4 col-md-6 mb-4">
                    <div class="card border-left-primary shadow h-100 py-2">
                      <a href="javascript:void(0)" @click="profitMetrics()">
                        <div class="card-body">
                            <div class="row no-gutters align-items-center">
                              <div class="col mr-2">
                                  <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                    Profit
                                  </div><br>
                                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                                    <small>Profit : <label class="text-success">${{ overallMatrics.profit }}</label></small><br>
                                    <small>Profit : <label class="text-success">{{  overallMatrics.profit_percentage }}%</label></small>
                                    
                                  </div>
                              </div>
                            </div>
                        </div>
                      </a>
                    </div>
                </div>
              </div>
              <div class="row">
                <div class="col-xl-12 col-md-12 mb-4">
                  <span class="text-danger font-weight-bold">
                      * Disclaimer: Income Tax is considered the same as Order Tax
                    </span>
                  <div class="float-right pb-2" v-if="tableData.length > 0">
                    <button type="button" class="btn btn-primary" :disabled="isDisabled" @click="exportTableData()">Export<i class="fas fa-spinner fa-spin" v-if="isDisabled"></i></button>
                  </div>
                    <table class="table table-hover table-striped mb-2">
                      <thead>
                      <tr>
                          <th style="width: 10%;" v-for="header in tableHeaders" :key="header">{{ header }}</th>
                      </tr>
                      </thead>
                      <tbody v-if="tableData.length > 0">
                          <tr v-for="data in tableData" :key="data.client_id">
                              <td v-for="value in data" :key="value">{{ value }}</td>
                          </tr>
                      </tbody>
                  </table>
                </div>
              </div>
              <!--<div class="row float-right">
                <div class="form-check">
                  <input class="form-check-input" type="radio" v-model="dateType" value="monthToDate" id="flexRadioDefault1" @change="dateFilterChanged()">
                  <label class="form-check-label" for="flexRadioDefault1">
                    Month to date &nbsp;&nbsp;
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" v-model="dateType" value="yearToDate"  id="flexRadioDefault2" @change="dateFilterChanged()">
                  <label class="form-check-label" for="flexRadioDefault2">
                    Year to date
                  </label>
                </div>
              </div><br><br>
              <div class="spinner-border" v-if="showLoader"></div>
              <div class="row" v-else>
                <div class="col-xl-3 col-md-6 mb-4">
                    <div class="card border-left-primary shadow h-100 py-2">
                      <div class="card-body">
                          <div class="row no-gutters align-items-center">
                            <div class="col mr-2">
                                <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                  Sass Fees
                                </div>
                                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ sassFees }}</div>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
                <div class="col-xl-3 col-md-6 mb-4">
                    <div class="card border-left-success shadow h-100 py-2">
                      <div class="card-body">
                          <div class="row no-gutters align-items-center">
                            <div class="col mr-2">
                                <div class="text-xs font-weight-bold text-success text-uppercase mb-1">
                                  Order Revenue
                                </div>
                                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ orderRevenue }}</div>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
                <div class="col-xl-3 col-md-6 mb-4">
                    <div class="card border-left-info shadow h-100 py-2">
                      <div class="card-body">
                          <div class="row no-gutters align-items-center">
                            <div class="col mr-2">
                                <div class="text-xs font-weight-bold text-danger text-uppercase mb-1">
                                  Order Return
                                </div>
                                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ orderReturn }}</div>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
                <div class="col-xl-3 col-md-6 mb-4">
                    <div class="card border-left-warning shadow h-100 py-2">
                      <div class="card-body">
                          <div class="row no-gutters align-items-center">
                            <div class="col mr-2">
                                <div class="text-xs font-weight-bold text-warning text-uppercase mb-1">
                                  Gross Profit
                                </div>
                                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ grossProfit }}</div>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
                <div class="col-xl-3 col-md-6 mb-4">
                    <div class="card border-left-primary shadow h-100 py-2">
                      <div class="card-body">
                          <div class="row no-gutters align-items-center">
                            <div class="col mr-2">
                                <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                  Points Allocated
                                </div>
                                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ pointsAllocated }}</div>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
                <div class="col-xl-3 col-md-6 mb-4">
                    <div class="card border-left-success shadow h-100 py-2">
                      <div class="card-body">
                          <div class="row no-gutters align-items-center">
                            <div class="col mr-2">
                                <div class="text-xs font-weight-bold text-success text-uppercase mb-1">
                                  Redemption Velocity
                                </div>
                                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ reduptionVelocity }}</div>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
                <div class="col-xl-3 col-md-6 mb-4">
                    <div class="card border-left-info shadow h-100 py-2">
                      <div class="card-body">
                          <div class="row no-gutters align-items-center">
                            <div class="col mr-2">
                                <div class="text-xs font-weight-bold text-danger text-uppercase mb-1">
                                  Total Order
                                </div>
                                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ totalOrder }}</div>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
                <div class="col-xl-3 col-md-6 mb-4">
                    <div class="card border-left-warning shadow h-100 py-2">
                      <div class="card-body">
                          <div class="row no-gutters align-items-center">
                            <div class="col mr-2">
                                <div class="text-xs font-weight-bold text-warning text-uppercase mb-1">
                                  Breakage
                                </div>
                                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ breakage }}</div>
                            </div>
                          </div>
                      </div>
                    </div>
                </div>
              </div>
              <ul class="nav nav-tabs mb-3" id="ex1" role="tablist">
                <li class="nav-item" role="presentation">
                    <a class="nav-link active" id="ex1-tab-1" data-toggle="tab" href="#ex1-tabs-1" role="tab"
                      aria-controls="ex1-tabs-1" aria-selected="true">User Points</a>
                </li>
                <li class="nav-item" role="presentation">
                    <a class="nav-link" id="ex1-tab-2" data-toggle="tab" href="#ex1-tabs-2" role="tab" >Breakage Transactions</a>
                </li>
              </ul>

              <div class="tab-content" id="ex1-content">
                <div class="tab-pane fade show active" id="ex1-tabs-1" role="tabpanel" aria-labelledby="ex1-tab-1">
                  <div class="rounded mt-3">
                    
                    <div class="d-flex mt-3" style="align-items:center;">
                      <div style="flex-grow:1;" class="pl-2">
                        <form class="form-inline">
                          <div class="form-group mb-2">
                            <input type="text" class="form-control" placeholder="Search by Name" v-model="clientName">
                          </div>
                          <button type="button" class="btn btn-primary mb-2" @click="getClientsPointsHistory()">Search</button>
                        </form>
                      </div>
                      <div class="form-group form-inline pr-2">
                        <span class="mr-1">Show </span>
                        <select v-model="PageSize" class="form-control">
                          <option :value="5">5</option>
                          <option :value="10">10</option>
                          <option :value="15">15</option>
                        </select>
                        <span class="ml-1"> entries</span>
                      </div>
                    </div>
                    <div class="spinner-border" v-if="showClientsPointsLoader"></div>
                    
                    <table class="table table-hover table-striped mb-2" v-else>
                      <thead>
                        <tr>
                          <th style="width: 10%;">Name</th>
                          <th style="width: 10%;">Total Users</th>
                          <th style="width: 10%;">Points Added ($)</th>
                          <th style="width: 10%;">Points Redeemed ($)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="clientPoint in clientsPoints" :key="clientPoint.ID">
                          <td>{{ clientPoint.name }}</td>
                          <td>{{ clientPoint.totalUsers }}</td>
                          <td>{{ clientPoint.pointAllocated }}</td>
                          <td>{{ clientPoint.pointSubtracted }}</td>
                        </tr>
                      </tbody>
                    </table>
                    
                    <div class="d-flex mb-2" style="align-items: center;">
                      <div style="flex-grow: 1;" class="pl-2">
                        <p>
                          Showing {{ (PageNumber - 1) * PageSize + 1 }} to {{ UpperLimit }}
                          of {{ TotalRecords }} entries
                        </p>
                      </div>
                      <div class="pr-2">
                        <Pagination
                        :data="clientsPoints"
                        :totalPages="Math.ceil(TotalRecords / PageSize)"
                        :total="TotalRecords"
                        :perPage="PageSize"
                        :currentPage="PageNumber"
                        @pagechanged="onClientsPointsTablePageChange"
                      />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="tab-pane fade" id="ex1-tabs-2" role="tabpanel" aria-labelledby="ex1-tab-2">
                    <div class="rounded mt-3">
                      <BreakageTransactionHistory :dateType="dateType" />
                    </div>
                </div>
              </div>-->
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>
<script>
import Pagination from "../../components/Pagination.vue";
import Header from "@/components/Header.vue";
import divvyAdminSidebar from "@/views/divvyadmin/divvyAdminSidebar.vue";
import DashboardService from "../../services/DashboardService"
import BreakageTransactionHistory from "../../components/BreakageTransactionHistory.vue";

export default {
  name: "divvyAdminDashboard",
  components: {
    appHeader: Header,
    appAdminSidebar: divvyAdminSidebar,
    Pagination,
    BreakageTransactionHistory
  },
  data() {
    return {
      sassFees: 0,
      orderRevenue: 0,
      orderReturn: 0,
      grossProfit: 0,
      pointsAllocated: 0,
      pointsSubtracted: 0,
      reduptionVelocity: 0,
      totalOrder:0,
      breakage:0,
      dateType: "monthToDate",
      showLoader: false,

      //Clients Points
      PageSize: 5,
      PageNumber: 1,
      TotalRecords: 0,
      UpperLimit:0,
      clientsPoints:[],
      clientName:"",
      showClientsPointsLoader: false,

      isDisabled: false,
      reportType: "",
      fromDate: "",
      toDate:"",
      allClients: [],
      tableHeaders: [],
      tableData: [],
      clientIds: [],
      overallMatrics: {
          "point_allocated_in_dollars": 0,
          "cogs": 0,
          "revenue": 0,
          "profit": 0,
          "profit_percentage": 0
      },
       showFormula: true,
    }
  },
  computed: {
  },
  methods: {
    getDashboardStats() {
        this.showLoader = true;
        let data = { dateType: this.dateType }
        DashboardService.getDashboardStats(data)
          .then((response) => {
            this.showLoader = false;
            this.sassFees = response.data.data.sassFees; 
            this.orderRevenue = response.data.data.orderRevenue; 
            this.orderReturn = response.data.data.orderReturn; 
            this.pointsAllocated = response.data.data.pointAllocated; 
            this.pointsSubtracted = response.data.data.pointSubtracted;
            if (this.pointsAllocated != 0) {
              this.reduptionVelocity =  parseFloat(this.pointsAllocated / this.pointsSubtracted).toFixed(2);
            }

            if (response.data.data.grossProfit.length > 0) {
              if (response.data.data.grossProfit[0].dollars) {
                this.grossProfit = (parseFloat(response.data.data.grossProfit[0].dollars)  - ( parseFloat(response.data.data.grossProfit[0].prodWholesale) + parseFloat(response.data.data.grossProfit[0].prodShipping))).toFixed(2)
              } else {
                this.grossProfit = 0;
              }
            }
            this.totalOrder = response.data.data.totalOrders;
            this.breakage = response.data.data.Breakage;
          })
          .catch((e) => {
            this.showLoader = false;
            this.$toast.error(e.response.data.respones_msg);
          });
      },
    getClientsPointsHistory() {
      this.showClientsPointsLoader = true;
      let data = { dateType: this.dateType, pageSize: this.PageSize, page: this.PageNumber, clientName: this.clientName }
      DashboardService.getClientsPointsHistory(data)
        .then((response) => {
          this.showClientsPointsLoader = false;
          this.clientsPoints = response.data.data.data;
          this.TotalRecords = response.data.data.total;
          this.UpperLimit = this.TotalRecords < this.PageNumber * this.PageSize ? this.TotalRecords : this.PageNumber * this.PageSize;
        })
        .catch((e) => {
          this.showClientsPointsLoader = false;
        });
    },
    onClientsPointsTablePageChange(page) {
      this.PageNumber = page;
      try {
        this.getClientsPointsHistory();
      } catch (error) {
        console.log(error);
      }
      window.scrollTo(0, 0);
    },
    dateFilterChanged() {
      this.getDashboardStats();
      this.getClientsPointsHistory();
    },
    async getOverallMetrics() {
      this.isDisabled = true
      let data = {
            start_date: this.fromDate,
            end_date: this.toDate,
            report_type: this.reportType,
            client_ids: this.clientIds,
        }
        await DashboardService.getOverallMetrics(data)
        .then((response) => {
            this.isDisabled = false;
            if (response.status == 200) {
              this.overallMatrics = response.data
            } else {
                alert(response.data.error)
            }
        }).catch((e) => {
          this.isDisabled = false;
          alert(e.response.data.message);
        });
    },
    async pointMetrics() {
      this.tableData = [];
      this.tableHeaders = [];
      this.isDisabled = true
      let data = {
            start_date: this.fromDate,
            end_date: this.toDate,
            report_type: this.reportType,
            client_ids: this.clientIds,
        }
        await DashboardService.pointMetrics(data)
        .then((response) => {
            this.isDisabled = false;
            if (response.status == 200) {
              this.tableData = (response.data.data) ? response.data.data : []
              this.tableHeaders = response.data.headers
            } else {
                alert(response.data.error)
            }
        }).catch((e) => {
          this.isDisabled = false;
          alert(e.response.data.message);
        });
    },
    async revenueMetrics() {
      this.tableData = [];
      this.tableHeaders = [];
      this.isDisabled = true
      let data = {
            start_date: this.fromDate,
            end_date: this.toDate,
            report_type: this.reportType,
            client_ids: this.clientIds,
        }
        await DashboardService.revenueMetrics(data)
        .then((response) => {
            this.isDisabled = false;
            if (response.status == 200) {
              this.tableData = (response.data.data) ? response.data.data : []
              this.tableHeaders = response.data.headers
            } else {
                alert(response.data.error)
            }
        }).catch((e) => {
          this.isDisabled = false;
          alert(e.response.data.message);
        });
    },
    async profitMetrics() {
      this.tableData = [];
      this.tableHeaders = [];
      this.isDisabled = true
      let data = {
            start_date: this.fromDate,
            end_date: this.toDate,
            report_type: this.reportType,
            client_ids: this.clientIds,
        }
        await DashboardService.profitMetrics(data)
        .then((response) => {
            this.isDisabled = false;
            if (response.status == 200) {
              this.tableData = (response.data.data) ? response.data.data : []
              this.tableHeaders = response.data.headers
            } else {
                alert(response.data.error)
            }
        }).catch((e) => {
          this.isDisabled = false;
          alert(e.response.data.message);
        });
    },
    async getAllClients() {
      let data = {
        }
        await DashboardService.getAllClients()
        .then((response) => {
            this.isDisabled = false;
            if (response.status == 200) {
              this.allClients = response.data
            } else {
                alert(response.data.error)
            }
        }).catch((e) => {
          this.isDisabled = false;
          alert(e.response.data.message);
        });
    },
    async exportTableData() {
      if (this.tableData.length > 0) {
        let exportData = [];
        for await (let data of this.tableData) {
          let objectData = [];
          for (var key in data) {
            objectData.push(data[key]);
          }
          exportData.push(objectData)
          //exportData.push([ data.email, data.errorCount, data.deletedDevices]);
        }
        //this.downloadSampleImportFile(this.tableHeaders.toString()+"\n", exportData);
        this.downloadSampleImportFile(this.tableHeaders.join('\t').split(',')+"\n", exportData);
      } else {
        alert("No data to export");
      }
    },
    downloadSampleImportFile(csv, exportData = "") {
        //let csv = 'Email,Error Count,Deleted Devices Count\n';
        if (exportData) {
          exportData.forEach((row) => {
              csv += row.join('\t');
              csv += "\n";
          });
        }
        const anchor = document.createElement('a');
        anchor.href = 'data:text/tsv;charset=utf-8,' + encodeURIComponent(csv);
        anchor.target = '_blank';
        if (exportData) {
          anchor.download = 'dashboard-data.tsv';
        }
        anchor.click();
      },
  },
  watch: {
    PageSize() {
      this.PageNumber = 1;
      this.getClientsPointsHistory();
    },
  },
  beforeMount() {
    this.getDashboardStats();
    this.getClientsPointsHistory();
    this.getAllClients();
  },
  mounted() {
     $('#clients-dropdown').select2({
        placeholder: "Select Client",
        theme: "classic",
        //closeOnSelect: false,
      }).on('select2:select', (e) => {
        this.clientIds.push(e.params.data.id)
        console.log(this.clientIds);
      }).on('select2:unselect', (e) => {
        this.clientIds = this.clientIds.filter(item => item !== e.params.data.id)
        console.log(this.clientIds);
      });
  }
};
</script>

<style lang="scss" scoped>
.admin-dashboard {
  // margin: 70px 0 0 0;
  min-height: 100%;
  margin: -14px 0 0 0;
  .badge {
    font-size: 0.8rem;
    padding: 6px 0px;
    border-radius: $border-radius-sm;
  }
}

html,
body {
  height: 100%;
}
</style>
